// ─────────────────────────────────────────────────────────────────────────
// Assistant registry — Agents v1 (static, presentation-layer only)
//
// The "Agents" group in CodeOSShell renders one profile card + tabbed page per
// entry in ASSISTANT_PROFILES. v1 ships ONE assistant: "The Operator", which is
// the existing chat harness presented as a first-class assistant. There is no
// per-customer storage and no `assistant_id` plumbing yet — every profile maps
// 1:1 onto the Agentic Operating System, so threads/actions/proposals/audits stay os_id-scoped (see
// the Agents spec §0/§5). A second assistant or per-customer overrides are what
// graduate this to a database table.
//
// `starter_prompts` are generic English for v1. Charter-localized generation
// (from os_charters.domain_context + active_schema.metadata) is a later pass —
// it belongs server-side, consistent with how the Operator's opening is already
// personalized. Keep the shape stable so that swap is additive.
// ─────────────────────────────────────────────────────────────────────────

export type AssistantBackend =
  // Reuses the existing harness chat loop (runQuery / /api/operator/chat).
  | { kind: "harness_chat"; provider: "gemini" | "anthropic"; model?: string }
  // A hosted media-generation API behind a server proxy + credit ledger (v2).
  | { kind: "media_gen"; api: "fal" | "replicate"; defaultModel: string };

export interface StarterPrompt {
  // Short chip text shown on the card.
  label: string;
  // The full message sent into the composer when the chip is tapped.
  prompt: string;
}

export interface AssistantProfile {
  id: string; // stable slug — also the nav entry id suffix
  display_name: string;
  role_description: string; // one strong line for the card
  engine_badge: string; // e.g. "Powered by Claude"
  backend: AssistantBackend;
  // Internal harness role this assistant maps onto for prompt + model defaults.
  harness_role: "operator" | "architect" | "verifier" | "subagent";
  capabilities: string[]; // plain-language, rendered as a tidy list
  starter_prompts: StarterPrompt[]; // tap-to-try chips
  skill_ids?: string[];
  workspace_blueprint?: {
    required_screens?: string[];
    starter_tasks?: string[];
    recommended_integrations?: string[];
    success_metrics?: string[];
  };
  studio_enabled?: boolean;
  studio_kind?: "code" | "content";
  // The "president" tier. A system assistant owns the workspace-wide surfaces
  // (structure + connections + evolution) and renders in the nav's System
  // section with a different tab set (Chat/Integrations/Evolution/Activity)
  // instead of the worker set (Chat/Workspace/Activity). The Architect is the
  // only system assistant; workers (Operator, Analyst) stay false.
  is_system?: boolean;
}

export const ASSISTANT_PROFILES: AssistantProfile[] = [
  {
    id: "operator",
    display_name: "The Operator",
    role_description:
      "Your day-to-day right hand — it answers questions about your data, manages records, and takes action in this workspace.",
    engine_badge: "Operations Specialist",
    backend: { kind: "harness_chat", provider: "anthropic" },
    harness_role: "operator",
    capabilities: [
      "Answer questions about your data",
      "Create and update records",
      "Triage what needs your attention",
      "Project likely outcomes",
      "Propose improvements to your workspace",
    ],
    skill_ids: [
      "workspace_data_read",
      "record_operations",
      "connector_discovery",
      "schema_evolution",
      "failure_diagnosis",
      "document_generation",
      "spreadsheet_analysis",
    ],
    workspace_blueprint: {
      required_screens: ["Dashboard", "Primary records", "Integrations", "Decisions"],
      starter_tasks: [
        "Ask where my first real data source lives, then route me to the smallest safe connector or approved local access path.",
        "Create one useful record from an example I give you and tell me where it was saved.",
        "Triage the workspace and name the first missing data, screen, permission, or Decision blocker.",
      ],
      success_metrics: [
        "Creates records directly when safe.",
        "Sensitive changes go through confirmation.",
        "Missing data turns into a connector or next-question path.",
      ],
    },
    starter_prompts: [
      { label: "What needs my attention?", prompt: "What needs my attention right now?" },
      { label: "How are things trending?", prompt: "Give me a quick read on how things are trending." },
      { label: "Add a record", prompt: "I'd like to add a new record — walk me through it." },
    ],
    is_system: false, // worker
  },
  {
    id: "analyst",
    display_name: "The Analyst",
    role_description:
      "Your numbers-first advisor — it reads your data, triages what needs attention, and projects where things are heading.",
    engine_badge: "Analytics Specialist",
    backend: { kind: "harness_chat", provider: "gemini" },
    harness_role: "operator",
    capabilities: [
      "Read and summarize your numbers",
      "Triage what needs attention this week",
      "Project where things are heading",
      "Flag risks with the evidence behind them",
    ],
    skill_ids: [
      "workspace_data_read",
      "screen_triage",
      "outlook_projection",
      "failure_diagnosis",
      "spreadsheet_analysis",
    ],
    workspace_blueprint: {
      required_screens: ["Dashboard", "Primary records", "Reports"],
      starter_tasks: [
        "Rank what needs attention this week using real rows and show the evidence behind each priority.",
        "Project the next 30 days with confidence, drivers, and missing assumptions.",
        "Find stale, risky, or incomplete records and tell me the smallest data fix needed.",
      ],
      success_metrics: [
        "Ranks priorities with evidence from rows.",
        "Shows confidence and drivers for projections.",
        "Explains when data is too thin to trust.",
      ],
    },
    starter_prompts: [
      { label: "What needs my attention this week?", prompt: "What needs my attention this week?" },
      { label: "Project our pipeline 30 days out", prompt: "Project our pipeline 30 days out." },
      { label: "Which cases are at risk?", prompt: "Which cases are at risk right now, and why?" },
    ],
    is_system: false, // worker
  },
  {
    id: "architect",
    display_name: "The Architect",
    role_description:
      "The structural engineer of the Agentic Operating System — describe what you want changed and it proposes the evolution for your approval.",
    engine_badge: "Systems Architect",
    backend: { kind: "harness_chat", provider: "anthropic" },
    harness_role: "architect",
    capabilities: [
      "Add or change screens",
      "Evolve your workflows",
      "Adjust your Agentic Operating System's structure",
      "Every change goes to your approval queue",
    ],
    skill_ids: [
      "workspace_data_read",
      "schema_evolution",
      "connector_discovery",
      "failure_diagnosis",
    ],
    workspace_blueprint: {
      required_screens: ["Decisions", "Integrations", "Evolution history", "Audit"],
      starter_tasks: [
        "Review this Agentic Operating System for missing screens, fields, agents, or permissions.",
        "Create a Decision for the smallest structural upgrade that improves repeatable work.",
        "Research whether a specialist agent or skill is needed before recommending deployment.",
      ],
      success_metrics: [
        "Every structural change becomes a Decision.",
        "New agents include skills, screens, data needs, and success criteria.",
        "Failed evolution explains the failed step and next unblock.",
      ],
    },
    starter_prompts: [
      { label: "Add a screen for tracking X", prompt: "Add a screen for tracking X." },
      { label: "Change how my pipeline stages work", prompt: "Change how my pipeline stages work." },
      { label: "What would you improve?", prompt: "What would you improve about my Agentic Operating System?" },
    ],
    // President tier — owns structure, connections, and evolution for the
    // whole workspace. Renders in the nav's System section.
    is_system: true,
  },
];

export function getAssistantProfile(id: string): AssistantProfile | undefined {
  return ASSISTANT_PROFILES.find((p) => p.id === id);
}

// ─────────────────────────────────────────────────────────────────────────
// Nav visibility — VISIBILITY ONLY. Hides an assistant's user-facing nav/roster
// entry points without touching the profile, is_system, systemProfile lookups,
// availableAssistantIds, or any internal navigateToAssistant() resolution. The
// profile stays fully active behind the scenes (Agent Library deploy, Evolution
// proposals, internal deep-links all unaffected).
// ─────────────────────────────────────────────────────────────────────────
export const HIDE_ARCHITECT_FROM_NAV = true;
const HIDDEN_NAV_IDS = HIDE_ARCHITECT_FROM_NAV ? new Set<string>(["architect"]) : new Set<string>();
export function isAssistantHiddenFromNav(id: string): boolean {
  return HIDDEN_NAV_IDS.has(id);
}
